import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, ChevronDown, ChevronUp, Download, RotateCcw, Search, Trash2 } from 'lucide-react';
import { partnerSessionsApi } from '../../../services/partnerSessionsApi';
import type { PartnerSessionSummary, SessionApp, SessionFileAnswer, SessionKeyAnswer, SessionRecipient, SessionResponseQuestion, SessionResponses } from '../../../types/aai';
import { AppMark } from '../../setup/setupKit';
import { ExportAction } from './ExportAction';
import { Action, Chip, ErrorNote, Input, Loading } from '../../../components/aa';
import type { ChipTone } from '../../../components/aa/Chip';
import { KIND_LABEL, ROLE_GROUPS, TYPE_LABEL, answerText, asApps, asFiles, asKey, asStrings, errorText, maskedKey, inGroup, matches, plural, roleLabel, shortDate } from './sessionShared';
import type { RoleGroup } from './sessionShared';

/*
 * Sessions: every session sent at this client, and for each one every
 * response, read by person or by question, filtered by role and searched by
 * name, email or department.
 */

type View = 'person' | 'question';

const percent = (n: number, total: number): number => {
  if (!total) return 0;
  return Math.round((n / total) * 100);
};

const Bar: React.FC<{ value: number }> = ({ value }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas-inset">
    <div className="h-full rounded-full bg-[#ffea00]" style={{ width: `${value}%` }} />
  </div>
);

const Segmented: React.FC<{ value: View; onChange: (v: View) => void }> = ({ value, onChange }) => {
  const items: { key: View; label: string }[] = [{ key: 'person', label: 'By person' }, { key: 'question', label: 'By question' }];
  return (
    <div className="inline-flex rounded-full bg-canvas-inset p-1" role="tablist">
      {items.map((it) => {
        let tone = 'text-ink-muted hover:text-ink';
        if (value === it.key) tone = 'bg-white text-ink shadow-sm';
        return (
          <button key={it.key} type="button" role="tab" aria-selected={value === it.key} onClick={() => onChange(it.key)} className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold ${tone}`}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

type OnDownload = (file: SessionFileAnswer) => void;

const FileLinks: React.FC<{ files: SessionFileAnswer[]; onDownload: OnDownload }> = ({ files, onDownload }) => (
  <span className="mt-1 flex flex-wrap gap-1.5">
    {files.map((f) => (
      <button key={f.id} type="button" onClick={() => onDownload(f)} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-line bg-white px-2.5 py-1 text-[12px] font-semibold text-ink hover:border-ink">
        <Download size={12} className="shrink-0" aria-hidden />
        <span className="truncate">{f.name}</span>
      </button>
    ))}
  </span>
);

const KEY_TONE: Record<string, ChipTone> = {
  queued: 'neutral',
  pending: 'run',
  set: 'ok',
  rejected: 'err',
};

type OnAskAgain = (email: string) => void;

const KeyLine: React.FC<{ answer: SessionKeyAnswer; onAskAgain?: () => void }> = ({ answer, onAskAgain }) => (
  <span className="mt-1 block">
    <span className="flex flex-wrap items-center gap-2">
      <code className="rounded-[6px] bg-canvas-inset px-2 py-0.5 font-mono text-[12.5px] text-ink">{maskedKey(answer)}</code>
      {answer.status_label && <Chip tone={KEY_TONE[answer.status || 'queued'] || 'neutral'}>{answer.status_label}</Chip>}
      {answer.status === 'rejected' && onAskAgain && <Action size="sm" variant="ghost" icon={<RotateCcw size={13} />} onClick={onAskAgain}>Ask again</Action>}
    </span>
    {answer.status === 'rejected' && answer.reason && <small className="mt-1 block text-[12px] text-ink-muted">Planner: {answer.reason}</small>}
  </span>
);

const hasRejectedKey = (person: SessionRecipient): boolean =>
  Object.values(person.answers).some((v) => asKey(v)?.status === 'rejected');

const PersonRow: React.FC<{ person: SessionRecipient; questions: SessionResponseQuestion[]; onDownload: OnDownload; onAskAgain: OnAskAgain }> = ({ person, questions, onDownload, onAskAgain }) => {
  const [open, setOpen] = useState(false);
  let status = <Chip tone="neutral">Waiting</Chip>;
  if (person.answered_at) status = <Chip tone="ok">Answered {shortDate(person.answered_at)}</Chip>;
  if (person.answered_at && hasRejectedKey(person)) status = <Chip tone="err">Key rejected</Chip>;
  let Icon = ChevronDown;
  if (open) Icon = ChevronUp;
  return (
    <div className="border-t border-line-soft first:border-t-0">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} disabled={!person.answered_at} className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-canvas disabled:cursor-default disabled:hover:bg-transparent">
        <span className="min-w-0 flex-1">
          <b className="block truncate text-[14px] text-ink">{person.name}</b>
          <small className="block truncate text-[12px] text-ink-dim">{[person.email, roleLabel(person.role), person.department].filter(Boolean).join(' · ')}</small>
        </span>
        {status}
        {person.answered_at && <Icon size={15} className="shrink-0 text-ink-dim" aria-hidden />}
      </button>
      {open && (
        <ul className="m-0 list-none space-y-3 bg-canvas px-5 py-4">
          {questions.map((q) => (
            <li key={q.id}>
              <p className="m-0 text-[12.5px] font-semibold text-ink-muted">{q.label}</p>
              {q.type === 'file' && asFiles(person.answers[q.id]).length > 0 && <FileLinks files={asFiles(person.answers[q.id])} onDownload={onDownload} />}
              {asKey(person.answers[q.id]) && <KeyLine answer={asKey(person.answers[q.id]) as SessionKeyAnswer} onAskAgain={() => onAskAgain(person.email)} />}
              {(q.type !== 'file' || asFiles(person.answers[q.id]).length === 0) && !asKey(person.answers[q.id]) && (
                <p className="m-0 mt-0.5 whitespace-pre-wrap text-[13.5px] text-ink">{answerText(q, person.answers[q.id]) || 'No answer'}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const QuestionBlock: React.FC<{ question: SessionResponseQuestion; number: number; answered: SessionRecipient[]; onDownload: OnDownload }> = ({ question: q, number, answered, onDownload }) => {
  const values = answered.map((p) => ({ person: p, value: p.answers[q.id] })).filter((x) => x.value !== undefined && x.value !== null && x.value !== '');
  let body: React.ReactNode = null;
  if (q.type === 'single' || q.type === 'multi') {
    body = (
      <div className="space-y-2">
        {(q.options || []).map((o) => {
          const n = values.filter((x) => {
            if (Array.isArray(x.value)) return asStrings(x.value).includes(o);
            return x.value === o;
          }).length;
          return (
            <div key={o}>
              <div className="mb-1 flex justify-between gap-3 text-[12.5px]"><span className="text-ink">{o}</span><span className="tabular-nums text-ink-muted">{n} · {percent(n, values.length)}%</span></div>
              <Bar value={percent(n, values.length)} />
            </div>
          );
        })}
      </div>
    );
  }
  if (q.type === 'scale') {
    const scores = values.map((x) => Number(x.value)).filter((v) => v >= 1 && v <= 5);
    let average = '–';
    if (scores.length) average = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
    body = (
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <b className="block font-mono text-[26px] leading-none text-ink">{average}</b>
          <small className="text-[11.5px] text-ink-muted">average of 5</small>
        </div>
        <div className="flex flex-1 items-end gap-2">
          {[1, 2, 3, 4, 5].map((s) => {
            const n = scores.filter((v) => v === s).length;
            return (
              <div key={s} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-[11px] tabular-nums text-ink-muted">{n}</span>
                <div className="flex h-16 w-full items-end rounded-[6px] bg-canvas-inset"><div className="w-full rounded-[6px] bg-[#ffea00]" style={{ height: `${percent(n, scores.length)}%` }} /></div>
                <span className="text-[11px] font-semibold text-ink">{s}</span>
              </div>
            );
          })}
        </div>
        {(q.low || q.high) && <small className="w-full text-[11.5px] text-ink-dim">1 = {q.low || '–'} · 5 = {q.high || '–'}</small>}
      </div>
    );
  }
  if (q.type === 'tools') {
    const counts = new Map<string, { app: SessionApp; count: number }>();
    values.forEach((x) => asApps(x.value).forEach((app) => {
      const row = counts.get(app.slug);
      if (row) row.count += 1;
      else counts.set(app.slug, { app, count: 1 });
    }));
    const ranked = Array.from(counts.values()).sort((a, b) => b.count - a.count);
    body = (
      <div className="space-y-2">
        {ranked.map(({ app, count }) => (
          <div key={app.slug} className="flex items-center gap-3">
            <AppMark app={app} />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex justify-between gap-3 text-[12.5px]"><span className="truncate text-ink">{app.label}</span><span className="shrink-0 tabular-nums text-ink-muted">{count} · {percent(count, values.length)}%</span></div>
              <Bar value={percent(count, values.length)} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (q.type === 'secret') {
    body = (
      <ul className="m-0 list-none space-y-2.5">
        {values.map((x) => {
          const key = asKey(x.value);
          if (!key) return null;
          return (
            <li key={x.person.email} className="rounded-[10px] bg-canvas px-3.5 py-2.5">
              <KeyLine answer={key} />
              <small className="mt-1 block text-[11.5px] text-ink-dim">{x.person.name} · {roleLabel(x.person.role)}</small>
            </li>
          );
        })}
      </ul>
    );
  }
  if (q.type === 'file') {
    body = (
      <ul className="m-0 list-none space-y-2.5">
        {values.map((x) => (
          <li key={x.person.email} className="rounded-[10px] bg-canvas px-3.5 py-2.5">
            <FileLinks files={asFiles(x.value)} onDownload={onDownload} />
            <small className="mt-1 block text-[11.5px] text-ink-dim">{x.person.name} · {roleLabel(x.person.role)}</small>
          </li>
        ))}
      </ul>
    );
  }
  if (q.type === 'text') {
    body = (
      <ul className="m-0 list-none space-y-2.5">
        {values.map((x) => (
          <li key={x.person.email} className="rounded-[10px] bg-canvas px-3.5 py-2.5">
            <p className="m-0 whitespace-pre-wrap text-[13.5px] text-ink">{String(x.value)}</p>
            <small className="mt-1 block text-[11.5px] text-ink-dim">{x.person.name} · {roleLabel(x.person.role)}</small>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="rounded-[14px] border border-line bg-white p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <strong className="text-[14.5px] text-ink">{number}. {q.label}</strong>
        <small className="text-[11.5px] text-ink-dim">{TYPE_LABEL[q.type]} · {plural(values.length, 'answer', 'answers')}</small>
      </div>
      {values.length === 0 && <p className="m-0 text-[13px] text-ink-dim">No answers from the people shown yet.</p>}
      {values.length > 0 && body}
    </div>
  );
};

const Responses: React.FC<{ clientId: string; sessionId: string; onBack: () => void; onDeleted: () => void }> = ({ clientId, sessionId, onBack, onDeleted }) => {
  const [data, setData] = useState<SessionResponses | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>('person');
  const [group, setGroup] = useState<RoleGroup>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null);
    partnerSessionsApi.responses(clientId, sessionId)
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => { if (alive) setError(errorText(err, 'Could not load the responses.')); });
    return () => { alive = false; };
  }, [clientId, sessionId]);

  const shown = useMemo(() => (data?.recipients || []).filter((p) => inGroup(p.role, group) && matches(query, p.name, p.email, p.department)), [data, group, query]);

  const download = async (file: SessionFileAnswer) => {
    try {
      const { data: blob } = await partnerSessionsApi.answerFile(clientId, sessionId, file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorText(err, 'Could not download that file.'));
    }
  };

  const askAgain = async (email: string) => {
    try {
      const r = await partnerSessionsApi.askAgain(clientId, sessionId, email);
      setData(r.data);
      toast.success(`Asked ${email} again. It is back in their AI Champion.`);
    } catch (err) {
      toast.error(errorText(err, 'Could not ask again.'));
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this session and every answer to it? People who have not answered will no longer see it.')) return;
    try {
      await partnerSessionsApi.remove(clientId, sessionId);
      toast.success('Session deleted.');
      onDeleted();
    } catch (err) {
      toast.error(errorText(err, 'Could not delete the session.'));
    }
  };

  const back = (
    <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-muted hover:text-ink">
      <ArrowLeft size={14} /> All sessions
    </button>
  );
  if (error) return <div>{back}<ErrorNote message={error} /></div>;
  if (!data) return <div>{back}<Loading label="Loading the responses" /></div>;
  const s = data.session;
  const answered = shown.filter((p) => p.answered_at);

  return (
    <div>
      {back}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="m-0 text-[22px] font-bold text-ink">{s.title}</h2>
          {s.description && <p className="m-0 mt-1 text-[13px] text-ink-muted">{s.description}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Chip tone="neutral">{KIND_LABEL[s.kind] || s.kind}</Chip>
            <Chip tone="neutral">Sent {shortDate(s.sent_at)}</Chip>
            <Chip tone="neutral">{plural(s.questions.length, 'question', 'questions')}</Chip>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <b className="block font-mono text-[22px] leading-none text-ink">{s.answered} / {s.recipients}</b>
            <small className="text-[11.5px] text-ink-muted">answered</small>
          </div>
          <ExportAction label="Export CSV" fileName={`session-${s.title}`} load={() => partnerSessionsApi.exportSession(clientId, sessionId)} />
          <Action size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={remove}>Delete</Action>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {ROLE_GROUPS.map((g) => {
            const count = data.recipients.filter((p) => inGroup(p.role, g.key)).length;
            let tone = 'border-line bg-white text-ink-muted hover:border-ink hover:text-ink';
            if (group === g.key) tone = 'border-ink bg-ink text-white';
            return (
              <button key={g.key} type="button" onClick={() => setGroup(g.key)} className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${tone}`}>
                {g.label} <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" aria-hidden />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people" className="!h-9 !w-[220px] !pl-9 !text-[12.5px]" aria-label="Search people" />
          </div>
          <Segmented value={view} onChange={setView} />
        </div>
      </div>

      {view === 'person' && (
        <div className="overflow-hidden rounded-[14px] border border-line bg-white">
          {shown.length === 0 && <p className="m-0 px-5 py-6 text-center text-[13px] text-ink-dim">Nobody matches this filter.</p>}
          {shown.map((p) => <PersonRow key={p.email} person={p} questions={s.questions} onDownload={download} onAskAgain={askAgain} />)}
        </div>
      )}
      {view === 'question' && (
        <div className="space-y-3">
          <p className="m-0 text-[12.5px] text-ink-muted">{plural(answered.length, 'answer', 'answers')} from the people shown.</p>
          {s.questions.map((q, i) => <QuestionBlock key={q.id} question={q} number={i + 1} answered={answered} onDownload={download} />)}
        </div>
      )}
    </div>
  );
};

export const SessionsView: React.FC<{ clientId: string; openId?: string; onOpen: (id: string) => void; onCreate: () => void }> = ({ clientId, openId, onOpen, onCreate }) => {
  const [sent, setSent] = useState<PartnerSessionSummary[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await partnerSessionsApi.list(clientId);
      setSent(r.data.sent);
    } catch (err) {
      setError(errorText(err, 'Could not load the sessions.'));
    }
  }, [clientId]);
  useEffect(() => { if (!openId) load(); }, [load, openId]);

  if (openId) return <Responses clientId={clientId} sessionId={openId} onBack={() => onOpen('')} onDeleted={() => onOpen('')} />;
  if (error) return <ErrorNote message={error} />;
  if (!sent) return <Loading label="Loading the sessions" />;
  if (sent.length === 0) {
    return (
      <div className="rounded-[14px] border border-dashed border-line-strong bg-white px-5 py-10 text-center">
        <strong className="block text-[15px] text-ink">No sessions sent yet</strong>
        <small className="mx-auto mt-1 block max-w-[48ch] text-[12.5px] text-ink-muted">Build a questionnaire or survey with the session agent, then send it to the people you choose. Their answers land here.</small>
        <div className="mt-4"><Action size="sm" onClick={onCreate}>Start a session</Action></div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-[12.5px] text-ink-muted">Every person's setup, journey and session answers in one file.</p>
        <ExportAction label="Export all responses" fileName="all-responses" load={() => partnerSessionsApi.exportResponses(clientId)} />
      </div>
      <div className="overflow-hidden rounded-[14px] border border-line bg-white">
        {sent.map((s) => (
          <button key={s.id} type="button" onClick={() => onOpen(s.id)} className="grid w-full grid-cols-1 items-center gap-3 border-t border-line-soft px-5 py-4 text-left first:border-t-0 hover:bg-canvas sm:grid-cols-[1.6fr_0.8fr_1fr]">
            <span className="min-w-0">
              <b className="block truncate text-[14px] text-ink">{s.title || 'Untitled session'}</b>
              <small className="block truncate text-[12px] text-ink-dim">{KIND_LABEL[s.kind] || s.kind} · {plural(s.questions.length, 'question', 'questions')} · sent {shortDate(s.sent_at)}</small>
            </span>
            <span className="text-[12.5px] text-ink-muted">{plural(s.recipients, 'person', 'people')}</span>
            <span className="flex items-center gap-3">
              <Bar value={percent(s.answered, s.recipients)} />
              <span className="w-14 shrink-0 text-right text-[12.5px] font-semibold tabular-nums text-ink">{s.answered} / {s.recipients}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SessionsView;
